import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton, Encabezado, Tarjeta } from '../componentes/ui/basicos';
import { Cargando, Problema, Vacio, useTextoDeError } from '../componentes/ui/estados';
import { Tabla, type Columna } from '../componentes/ui/tabla';
import { conDosDecimales } from '../venta/PiezasDeVenta';

interface Armable {
  readonly varianteId: string;
  readonly nombre: string | null;
  readonly cuantos: number;
}

interface Componente {
  readonly id: string;
  readonly componenteId: string;
  readonly nombre: string | null;
  readonly cantidad: string;
}

interface Armado {
  readonly id: string;
  readonly varianteId: string;
  readonly nombre: string | null;
  readonly cantidad: string;
  readonly costoTotal: string;
  readonly deshaceAId: string | null;
  readonly ocurridoEn: string;
  readonly correoDelUsuario: string | null;
}

interface Deposito {
  readonly id: string;
  readonly nombre: string;
}

/**
 * Armado de combos.
 *
 * La pantalla se lee de izquierda a derecha como se hace el trabajo: **de qué se compone** y
 * después **cuántos armo**. La receta no se edita acá —se carga por la ruta— porque la decisión de
 * qué lleva un combo es de catálogo y se toma una vez; lo que se hace todos los días es armar.
 *
 * El costo se muestra siempre, y es lo que la entrega vino a dar: un combo que se vende a un precio
 * sin saber qué costó es un margen inventado.
 */
export function Produccion(): React.ReactElement {
  const { t } = useTranslation();
  const cola = useQueryClient();
  const textoDeError = useTextoDeError();
  const [elegida, setElegida] = useState<string | null>(null);
  const [cantidad, setCantidad] = useState('1');
  const [depositoId, setDepositoId] = useState('');

  const armables = useQuery({
    queryKey: ['produccion-armables'],
    queryFn: () => pedir<Armable[]>('/produccion/armables'),
  });
  const depositos = useQuery({
    queryKey: ['depositos'],
    queryFn: () => pedir<Deposito[]>('/depositos'),
  });
  const armados = useQuery({
    queryKey: ['produccion-armados'],
    queryFn: () => pedir<Armado[]>('/produccion/armados'),
  });
  const receta = useQuery({
    queryKey: ['produccion-receta', elegida],
    queryFn: () => pedir<Componente[]>(`/produccion/recetas/${elegida ?? ''}`),
    enabled: elegida !== null,
  });

  const refrescar = (): void => {
    for (const clave of ['produccion-armados', 'existencias']) {
      void cola.invalidateQueries({ queryKey: [clave] });
    }
  };

  const armar = useMutation({
    mutationFn: () =>
      pedir<{ costoTotal: string }>('/produccion/armados', {
        method: 'POST',
        cuerpo: {
          varianteId: elegida,
          depositoId: depositoId === '' ? (depositos.data?.[0]?.id ?? '') : depositoId,
          cantidad,
        },
      }),
    onSuccess: refrescar,
  });

  const desarmar = useMutation({
    mutationFn: (id: string) =>
      pedir<{ costoTotal: string }>(`/produccion/armados/${id}/deshacer`, { method: 'POST' }),
    onSuccess: refrescar,
  });

  if (armables.isPending) return <Cargando />;
  if (armables.isError) return <Problema error={armables.error} />;

  const columnasDeArmados: Columna<Armado>[] = [
    { clave: 'que', titulo: t('produccion.combo'), contenido: (fila) => fila.nombre },
    {
      clave: 'cuantos',
      titulo: t('produccion.cantidad'),
      numerica: true,
      contenido: (fila) => conDosDecimales(fila.cantidad),
    },
    {
      clave: 'costo',
      titulo: t('produccion.costo'),
      numerica: true,
      contenido: (fila) => conDosDecimales(fila.costoTotal),
    },
    { clave: 'quien', titulo: t('produccion.quien'), contenido: (fila) => fila.correoDelUsuario },
    {
      clave: 'acciones',
      titulo: '',
      // Un desarmado no se desarma: sólo se puede deshacer lo que armó.
      contenido: (fila) =>
        fila.deshaceAId === null ? (
          <Boton
            tono="secundario"
            cargando={desarmar.isPending}
            onClick={() => {
              desarmar.mutate(fila.id);
            }}
          >
            {t('produccion.desarmar')}
          </Boton>
        ) : (
          <span className="text-xs text-texto-tenue">{t('produccion.esUnDesarmado')}</span>
        ),
    },
  ];

  return (
    <section className="flex flex-col gap-4">
      <Encabezado titulo={t('produccion.titulo')} descripcion={t('produccion.descripcion')} />

      {armables.data.length === 0 ? (
        <Tarjeta>
          <Vacio titulo={t('produccion.sinRecetas')} descripcion={t('produccion.comoSeCarga')} />
        </Tarjeta>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <Tarjeta>
            <h2 className="mb-2 text-sm font-semibold">{t('produccion.recetas')}</h2>
            <Tabla
              filas={armables.data}
              claveDeFila={(fila) => fila.varianteId}
              columnas={[
                { clave: 'que', titulo: t('produccion.combo'), contenido: (fila) => fila.nombre },
                {
                  clave: 'cuantos',
                  titulo: t('produccion.componentes'),
                  numerica: true,
                  contenido: (fila) => fila.cuantos,
                },
                {
                  clave: 'acciones',
                  titulo: '',
                  contenido: (fila) => (
                    <Boton
                      tono={elegida === fila.varianteId ? 'principal' : 'secundario'}
                      onClick={() => {
                        setElegida(fila.varianteId);
                      }}
                    >
                      {t('produccion.elegir')}
                    </Boton>
                  ),
                },
              ]}
            />
          </Tarjeta>

          <Tarjeta>
            <h2 className="mb-2 text-sm font-semibold">{t('produccion.armar')}</h2>
            {elegida === null ? (
              <Vacio titulo={t('produccion.elegiUno')} />
            ) : (
              <>
                <Tabla
                  filas={receta.data ?? []}
                  claveDeFila={(fila) => fila.id}
                  columnas={[
                    {
                      clave: 'que',
                      titulo: t('produccion.componente'),
                      contenido: (fila) => fila.nombre,
                    },
                    {
                      clave: 'cuantos',
                      titulo: t('produccion.porCombo'),
                      numerica: true,
                      contenido: (fila) => conDosDecimales(fila.cantidad),
                    },
                  ]}
                />
                <div className="mt-3 flex flex-wrap items-end gap-3">
                  <label className="flex w-28 flex-col gap-1 text-xs text-texto-tenue">
                    {t('produccion.cantidad')}
                    <input
                      type="text"
                      inputMode="decimal"
                      value={cantidad}
                      onChange={(evento) => {
                        setCantidad(evento.target.value);
                      }}
                      className="rounded-marco border border-borde bg-superficie px-2 py-1 text-sm text-texto"
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-xs text-texto-tenue">
                    {t('produccion.deposito')}
                    <select
                      value={depositoId}
                      onChange={(evento) => {
                        setDepositoId(evento.target.value);
                      }}
                      className="rounded-marco border border-borde bg-superficie px-2 py-1 text-sm text-texto"
                    >
                      {(depositos.data ?? []).map((deposito) => (
                        <option key={deposito.id} value={deposito.id}>
                          {deposito.nombre}
                        </option>
                      ))}
                    </select>
                  </label>
                  <Boton
                    tono="principal"
                    cargando={armar.isPending}
                    onClick={() => {
                      armar.mutate();
                    }}
                  >
                    {t('produccion.armar')}
                  </Boton>
                </div>
                {armar.isError ? (
                  <p className="mt-2 text-xs text-mal">{textoDeError(armar.error)}</p>
                ) : null}
                {armar.isSuccess ? (
                  <p className="mt-2 text-xs text-bien">
                    {t('produccion.armado', { costo: conDosDecimales(armar.data.costoTotal) })}
                  </p>
                ) : null}
                {/* El costo del combo no se elige: sale de las capas que se consumieron. */}
                <p className="mt-2 text-xs text-texto-tenue">{t('produccion.costoSaleDeAhi')}</p>
              </>
            )}
          </Tarjeta>
        </div>
      )}

      <Tarjeta>
        <h2 className="mb-2 text-sm font-semibold">{t('produccion.historial')}</h2>
        {desarmar.isError ? (
          <p className="mb-2 text-xs text-mal">{textoDeError(desarmar.error)}</p>
        ) : null}
        {(armados.data ?? []).length === 0 ? (
          <Vacio titulo={t('produccion.sinArmados')} />
        ) : (
          <Tabla
            filas={armados.data ?? []}
            claveDeFila={(fila) => fila.id}
            columnas={columnasDeArmados}
          />
        )}
      </Tarjeta>
    </section>
  );
}
